import { motion } from 'framer-motion';
import { MaskedRevealOnView, FadeUp } from './motion/Reveal';

const FOUNDER_PHOTO = 'https://customer-assets-7cd3h4nn.emergentagent.net/job_bright-steel-pro/artifacts/6gmgjkgm_13D5EF43-C332-4FB0-B022-A68A0D50783D.png';

const PARAGRAPHS = [
    'From the very beginning, our vision has been simple — to build a company that stands for uncompromising quality, unwavering trust, and long-term relationships. Every bright bar that leaves our facility reflects our commitment to precision, consistency, and excellence.',
    'We believe that true success is not measured by the volume we produce, but by the confidence our customers place in us. That confidence is earned through strict quality control, timely deliveries, transparent business practices, and a relentless pursuit of continuous improvement.',
    'Our mission is to be more than just a supplier. We strive to become a trusted partner who understands our customers\' needs and delivers products that consistently meet the highest standards of performance and reliability.',
    'As we continue to grow, our values remain unchanged: integrity in every decision, quality in every product, and trust in every relationship. These principles guide our team every day and inspire us to raise the benchmark for the bright bar industry.',
    'Thank you for your trust and confidence in us. We look forward to building a stronger future — together.',
];

const Founder = () => {
    return (
        <section id="founder" data-testid="founder-section" className="bg-[#0A0A0B] text-white py-28 md:py-40 border-t border-white/10 relative overflow-hidden">
            {/* Blueprint grid */}
            <div
                className="absolute inset-0 pointer-events-none opacity-[0.04]"
                style={{
                    backgroundImage:
                        'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
                    backgroundSize: '80px 80px',
                }}
            />

            <div className="relative mx-auto max-w-[1500px] px-6 md:px-10">
                <div className="grid grid-cols-12 gap-6 mb-16">
                    <div className="col-span-12 md:col-span-4">
                        <span className="overline text-[#FF3B00]">Letter · from the founder</span>
                    </div>
                    <div className="col-span-12 md:col-span-8">
                        <MaskedRevealOnView
                            className="h-display text-[10vw] md:text-[5.8vw] leading-[0.92]"
                            lines={['"Steel is more', 'than a product —', 'it is a promise."']}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-8 lg:gap-16 border-t border-white/15 pt-14 items-start">
                    {/* Portrait column */}
                    <div className="col-span-12 lg:col-span-5">
                        <motion.div
                            initial={{ opacity: 0, scale: 1.04 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true, margin: '-80px' }}
                            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                            className="relative overflow-hidden clip-corner bg-black"
                        >
                            <div className="aspect-[4/5] relative">
                                <img
                                    src={FOUNDER_PHOTO}
                                    alt="Founder — Indian Industrial Distributors"
                                    className="w-full h-full object-cover"
                                />
                                {/* Deep-shadow spotlight */}
                                <div
                                    className="absolute inset-0 pointer-events-none"
                                    style={{
                                        background:
                                            'radial-gradient(ellipse at 55% 35%, transparent 30%, rgba(0,0,0,0.35) 75%, rgba(0,0,0,0.7) 100%)',
                                    }}
                                />
                                <div className="absolute top-4 left-4 overline text-white/70">EST. 1985</div>
                                <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                                    <div>
                                        <div className="overline text-white/60 mb-1">Founder · Chairman</div>
                                        <div className="h-display text-3xl md:text-4xl leading-[0.95]">Gaurishankar<br />Ghuwalewala</div>
                                    </div>
                                    <div className="text-[#FF3B00] text-4xl">✱</div>
                                </div>
                            </div>
                        </motion.div>

                        <FadeUp delay={0.2}>
                            <div className="mt-6 grid grid-cols-3 border-t border-white/15">
                                <div className="p-4 border-r border-white/15">
                                    <div className="h-display text-2xl md:text-3xl">40+</div>
                                    <div className="overline text-white/50 mt-1">Years</div>
                                </div>
                                <div className="p-4 border-r border-white/15">
                                    <div className="h-display text-2xl md:text-3xl">3</div>
                                    <div className="overline text-white/50 mt-1">Generations</div>
                                </div>
                                <div className="p-4">
                                    <div className="h-display text-2xl md:text-3xl">1</div>
                                    <div className="overline text-white/50 mt-1">Standard</div>
                                </div>
                            </div>
                        </FadeUp>
                    </div>

                    {/* Letter column */}
                    <div className="col-span-12 lg:col-span-7">
                        {/* Opening pull quote */}
                        <FadeUp>
                            <div className="flex items-start gap-4 mb-10">
                                <span className="h-display text-[#FF3B00] text-6xl md:text-8xl leading-none">"</span>
                                <p className="h-display text-xl md:text-3xl leading-[1.15] max-w-xl text-white/90">
                                    At our company, steel is more than a product — it is a promise.
                                </p>
                            </div>
                        </FadeUp>

                        <div className="space-y-6 md:space-y-7 max-w-xl">
                            {PARAGRAPHS.map((para, i) => (
                                <FadeUp key={i} delay={0.05 + i * 0.06}>
                                    <div className="flex gap-5 items-start">
                                        <span className="overline text-[#FF3B00] mt-1.5 hidden md:block">0{i + 1}</span>
                                        <p className="text-white/75 text-base md:text-[17px] leading-[1.7]">{para}</p>
                                    </div>
                                </FadeUp>
                            ))}
                        </div>

                        {/* Signature */}
                        <FadeUp delay={0.4}>
                            <div className="mt-14 pt-8 border-t border-white/15 max-w-xl flex items-end justify-between">
                                <div>
                                    <div className="h-display text-3xl md:text-4xl italic tracking-tight" style={{ fontFamily: '"Playfair Display", "Clash Display", serif' }}>
                                        With gratitude,
                                    </div>
                                    <div className="overline text-white/50 mt-4">Gaurishankar Ghuwalewala · Founder · Chairman</div>
                                </div>
                                <div className="hidden md:block text-white/40 overline">
                                    Thane · MH · India
                                </div>
                            </div>
                        </FadeUp>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Founder;
